/*
  Kkumi Earn Points — shared client for every "you'll earn X points" tracker.

  The effective points-per-dollar = the rewards app's earn rate from
  /apps/rewards/program × the signed-in customer's tier multiplier from
  /apps/rewards/balance, × the best live campaign multiplier (+ any flat
  campaign bonus) from /apps/rewards/campaigns. All three are App Proxy URLs
  signed by Shopify, so they're the single source of truth.

  Campaign effect mirrors the orders/paid webhook exactly (campaignEffect below
  is a line-for-line port of apps/rewards/src/lib/campaigns.js, held together by
  apps/rewards/test/earn-preview-parity.test.js): the best multiplier wins (they
  don't stack), flat bonuses sum, and each only applies when the previewed
  (earn-eligible) total meets that campaign's min_order_cents. So a live "2×
  points this weekend" shows doubled points everywhere a base number used to
  show. A campaign with conditions (`rules`: the order must include an item,
  chosen tiers, a first order, …) counts only when the preview knows enough:
  the cart and product page pass their lines; a signed-in member's ineligible
  campaigns come from /balance (campaigns_blocked); a guest is previewed as a
  brand-new member. A caller without lines never shows an item bonus.

  The same data drives the product-page campaign callout
  ([data-kk-campaign-callout], snippets/kkumi-campaign-callout.liquid): one chip
  per campaign tied to the product, copy from the theme editor.

  Currency: every total handed in here (Liquid prices, /cart.js, FBT sums) is
  in the shopper's currency, but the earn rate, tier spend and campaign
  minimums are per SHOP-currency dollar, and orders/paid credits the
  shop-currency subtotal. So the maths runs on KKMoney.toShopCents(total) —
  without it a ¥2,500 kit previewed 2,500 "dollars" of points. If the amount
  can't be converted the tracker stays loading rather than show a guess.

  Scopes:
    cart    — total is cart.total_price (in cents); refreshed on cart mutations.
    product — total is the selected variant price; refreshed on PUB_SUB_EVENTS.variantChange.
    bundle  — total is updated by the host section via KkumiEarnPoints.setTotal(el, cents).

  Until both endpoints resolve every tracker stays in a loading state — we
  never render a "fallback" number that might be wrong by a tier multiple.
*/
window.KkumiEarnPoints = (function () {
  // The Polco Points coin badge, shown immediately before every previewed
  // number. Lockstep with snippets/kkumi-polco-coin.liquid (the Liquid twin) —
  // change one, change the other. Sizing/layout live in kkumi-earn-points.css.
  var COIN_SVG = '<svg class="kkumi-earn-points__coin" viewBox="6 6 108 108" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false"><circle cx="62" cy="64" r="48" fill="#000" opacity="0.16"/><circle cx="58" cy="56" r="48" fill="#c94a7b"/><circle cx="58" cy="56" r="42" fill="#f279a1"/><circle cx="58" cy="56" r="41" fill="none" stroke="#fff" stroke-opacity="0.9" stroke-width="3.6" stroke-dasharray="0 8.6" stroke-linecap="round"/><path d="M58.0,29.0 L64.7,49.3 L85.0,56.0 L64.7,62.7 L58.0,83.0 L51.3,62.7 L31.0,56.0 L51.3,49.3 Z" fill="#fff"/></svg>';

  var trackers = [];
  var ratePromise = null;
  var rate = null; // { perDollar, multiplier } once resolved
  var lastCartTotal = null;
  var lastCartLines = null; // the cart's lines (storefront cents), excluded add-ons out
  var cartListenersInstalled = false;
  var callouts = [];

  function shopifyRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function fetchRate() {
    if (ratePromise) return ratePromise;
    var base = shopifyRoot().replace(/\/$/, '');
    // Read through the shared client cache (window.KKRewards) when present so
    // /program + /campaigns (+ the /balance version probe) aren't refetched on
    // every product/cart page when nothing changed; fall back to a direct fetch.
    function rget(path, scope) {
      if (window.KKRewards) return window.KKRewards.get(path, { scope: scope }).catch(function () { return null; });
      return fetch(base + '/apps/rewards' + path, { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; });
    }
    ratePromise = Promise.all([
      rget('/program', 'public'),
      rget('/balance', 'member'),
      // Live campaigns are non-fatal: if this fails we still preview the base
      // (tier-adjusted) points rather than blocking the tracker.
      rget('/campaigns', 'public')
    ]).then(function (res) {
      var prog = res[0], bal = res[1], camp = res[2];
      var perDollar = prog && prog.points && Number(prog.points.earn_per_dollar);
      if (!Number.isFinite(perDollar) || perDollar <= 0) return null;
      var mult = bal && bal.tier && Number(bal.tier.multiplier);
      var excluded = (prog && prog.excluded_variant_ids) || [];
      rate = {
        perDollar: perDollar,
        multiplier: (Number.isFinite(mult) && mult > 0) ? mult : 1,
        // Tier NAME, so the rate line can name the rate that's applying
        // ("15 pts per $1 · Idol rate") instead of leaving the shopper to
        // reconcile it against a footer quoting the base rate.
        tierName: (bal && bal.tier && bal.tier.name) || '',
        // Club gate: signed in but not in the Kkumi Club → no points earned,
        // so trackers show the join prompt instead. An older worker without
        // the field reads as a member.
        notClub: !!(bal && bal.logged_in && bal.club_member === false),
        excluded: Array.isArray(excluded) ? excluded.map(String) : [],
        // `signup_bonus` fallback: tolerates a worker still on the
        // pre-2026-07-25 key while the theme is already live.
        secondOrder: (prog && prog.points && Number(prog.points.second_order_bonus != null ? prog.points.second_order_bonus : prog.points.signup_bonus)) || 0,
        loggedIn: !!(bal && bal.logged_in),
        // Live campaigns this member can't earn (wrong tier, already used, …). An
        // older worker without the field leaves conditional campaigns out.
        blocked: (bal && Array.isArray(bal.campaigns_blocked)) ? bal.campaigns_blocked.map(String) : null,
        campaigns: normaliseCampaigns(camp && Array.isArray(camp.campaigns) ? camp.campaigns : [])
      };
      // The Liquid seed (cart.total_price) can't know about excluded add-ons or
      // the cart's items; once we need either, re-pull the cart so previews are exact.
      if (rate.excluded.length || rate.campaigns.some(function (c) { return !!c.rule; })) refreshCartTotal();
      renderCallouts();
      return rate;
    });
    return ratePromise;
  }

  // A storefront amount (shopper's currency, cents) in shop-currency cents —
  // the unit every rule below is written in. Null when it can't be converted.
  // KKMoney loads head-defer before this file; the fallback only covers a page
  // where it didn't, and refuses to treat a foreign amount as the shop's.
  function shopCents(cents) {
    var n = Number(cents) || 0;
    if (window.KKMoney && typeof window.KKMoney.toShopCents === 'function') return window.KKMoney.toShopCents(n);
    var c = window.KKMoneyConfig;
    return (c && c.currency && c.shopCurrency && c.currency !== c.shopCurrency) ? null : n;
  }

  // /campaigns rows → what the maths reads. Only campaigns that move points stay
  // (banner- or discount-only ones don't change the number). A campaign with no
  // conditions carries multiplier / bonus_points; one with conditions carries
  // `rules` instead (and null in those two, so an older copy of this script
  // can't treat it as store-wide).
  function normaliseCampaigns(list) {
    var out = [];
    list.forEach(function (c) {
      var r = c && c.rules;
      var type = r ? r.type : (Number(c.multiplier) > 1 ? 'multiplier' : (Number(c.bonus_points) > 0 ? 'bonus' : null));
      var value = r ? Number(r.value) : (type === 'multiplier' ? Number(c.multiplier) : Number(c.bonus_points));
      if (!(type === 'multiplier' && value > 1) && !(type === 'bonus' && value > 0)) return;
      var rule = null;
      if (r && r.items) {
        rule = { p: {}, v: {}, minQty: Math.max(1, Math.trunc(Number(r.items.min_qty)) || 1) };
        (r.items.product_ids || []).forEach(function (id) { rule.p[String(id)] = true; });
        (r.items.variant_ids || []).forEach(function (id) { rule.v[String(id)] = true; });
      }
      out.push({
        id: String(c.id), type: type, value: value, scope: r && r.scope === 'items' ? 'items' : 'order',
        min: Number(c.min_order_cents) || 0, rule: rule, audience: (r && r.audience) || null,
        titles: (r && r.items && r.items.titles) || [], endsAt: Number(c.ends_at) || null
      });
    });
    return out;
  }

  // Can this shopper earn the campaign? Conditions on who (tiers, first order,
  // comeback, once per member) are the worker's call for a signed-in member
  // (/balance campaigns_blocked); a guest is previewed as a brand-new member.
  function eligible(c) {
    if (!c.audience) return true;
    if (rate.loggedIn) return !!rate.blocked && rate.blocked.indexOf(c.id) === -1;
    return !!c.audience.new_member;
  }

  // lib/points.js pointsForSubtotal: floor(dollars × rate × multiplier).
  function pts(cents, mult) {
    if (!(cents > 0)) return 0;
    if (!(mult > 0)) mult = 1;
    return Math.floor((cents / 100) * rate.perDollar * mult);
  }

  function beats(m, id, best) { return m > best.m || (m === best.m && best.id != null && String(id) < String(best.id)); }

  // The lines an item rule matches ($0 lines never count), as lib/campaign-items.js matchLines.
  function matchLines(rule, lines) {
    var out = { qty: 0, lines: {} };
    (lines || []).forEach(function (l) {
      if (!(l.cents > 0)) return;
      if (!((l.productId && rule.p[String(l.productId)]) || (l.variantId && rule.v[String(l.variantId)]))) return;
      var prev = out.lines[l.id] || [0, 0];
      out.lines[l.id] = [prev[0] + l.qty, prev[1] + l.cents];
      out.qty += l.qty;
    });
    return out;
  }

  // The campaigns' effect on an order of `sub` SHOP-currency cents with `lines`
  // (null when the caller can't say what's in it). Port of campaignEffect in
  // apps/rewards/src/lib/campaigns.js: { mult: the best plain multiplier,
  // bonus: flat bonuses + every item-tied point }.
  function campaignEffect(sub, lines) {
    sub = Math.max(0, Math.trunc(sub) || 0);
    var plain = [], withItems = [];
    ((rate && rate.campaigns) || []).forEach(function (c) {
      if (sub < c.min || !eligible(c)) return;
      (c.rule || c.scope === 'items' ? withItems : plain).push(c);
    });
    var best = { m: 1, id: null }, bonus = 0;
    plain.forEach(function (c) {
      if (c.type === 'multiplier') { if (beats(c.value, c.id, best)) best = { m: c.value, id: c.id }; }
      else bonus += Math.trunc(c.value);
    });
    if (!withItems.length || !lines) return { mult: best.m, bonus: bonus };
    var tier = rate.multiplier;
    var earned = pts(sub, tier * best.m);
    var hits = [];
    withItems.forEach(function (c) {
      if (!c.rule) return;
      var matched = matchLines(c.rule, lines);
      if (matched.qty >= c.rule.minQty) hits.push({ c: c, matched: matched });
    });
    hits.forEach(function (h) {
      if (h.c.type === 'bonus') bonus += h.c.scope === 'items' ? Math.trunc(h.c.value) * h.matched.qty : Math.trunc(h.c.value);
    });
    var lift = { m: best.m, id: null, hit: null };
    hits.forEach(function (h) {
      if (h.c.type === 'multiplier' && h.c.scope === 'order' && h.c.value > best.m && beats(h.c.value, h.c.id, lift)) lift = { m: h.c.value, id: h.c.id, hit: h };
    });
    if (lift.hit) {
      var extra = pts(sub, tier * lift.m) - earned;
      if (extra > 0) bonus += extra;
    }
    var orderM = lift.hit ? lift.m : best.m;
    var lineMults = hits.filter(function (h) { return h.c.type === 'multiplier' && h.c.scope === 'items' && h.c.value > orderM; });
    var lineIds = [];
    lineMults.forEach(function (h) { Object.keys(h.matched.lines).forEach(function (id) { if (lineIds.indexOf(id) === -1) lineIds.push(id); }); });
    var wins = [], winAt = {};
    lineIds.forEach(function (id) {
      var w = null;
      lineMults.forEach(function (h) {
        if (!h.matched.lines[id]) return;
        if (!w || beats(h.c.value, h.c.id, { m: w.c.value, id: w.c.id })) w = h;
      });
      if (winAt[w.c.id] == null) { winAt[w.c.id] = wins.length; wins.push({ hit: w, cents: 0 }); }
      wins[winAt[w.c.id]].cents += w.matched.lines[id][1];
    });
    var room = sub;
    wins.forEach(function (e) {
      var cents = Math.min(e.cents, room);
      room -= cents;
      var extra = pts(cents, tier * (e.hit.c.value - orderM));
      if (extra > 0) bonus += extra;
    });
    return { mult: best.m, bonus: bonus };
  }

  // A tracker's lines (storefront cents) in shop cents, or null when there are
  // none to read or one can't be converted (item campaigns then don't count).
  function shopLines(lines) {
    if (!Array.isArray(lines)) return null;
    var out = [];
    for (var i = 0; i < lines.length; i++) {
      var c = shopCents(lines[i].cents);
      if (c == null) return null;
      out.push({ id: String(lines[i].id), productId: lines[i].productId, variantId: lines[i].variantId, qty: Number(lines[i].qty) || 1, cents: c });
    }
    return out;
  }

  // Points previewed for an earn-eligible total (cents, shopper's currency) and,
  // when known, its lines. Null until the rate resolves, or if the total can't be
  // converted. Mirrors the webhook: floor(shop dollars × perDollar × tier ×
  // campaign), then the campaign points, which only ride an order that earns.
  function pointsFor(cents, lines) {
    if (rate == null) return null;
    var shop = shopCents(cents);
    if (shop == null) return null;
    var cf = campaignEffect(shop, shopLines(lines));
    var earned = pts(shop, rate.multiplier * cf.mult);
    return earned + (earned > 0 ? cf.bonus : 0);
  }

  // The points-per-dollar actually applying to this shopper right now: the
  // program's base rate x their tier multiplier x the best live campaign
  // multiplier for this total. Flat campaign bonuses are deliberately NOT
  // folded in — they're a one-off top-up, not a rate. Null until the rate
  // resolves. Trimmed to at most 2 decimals ("13.5", not "13.500000000001").
  function effectiveRate(cents) {
    if (rate == null) return null;
    var shop = shopCents(cents);
    if (shop == null) return null;
    var eff = rate.perDollar * rate.multiplier * campaignEffect(shop, null).mult;
    return Math.round(eff * 100) / 100;
  }

  // The small "how you're earning" line beside an estimate. Merchant copy
  // (data-rate-pattern) with [rate] -> the effective per-dollar rate and
  // [tier] -> the shopper's tier name. Members whose tier or a campaign lifts
  // them above the base rate are the whole point of it, but it renders for
  // everyone the pattern is set on, so a guest sees the rate they'd be on.
  function renderRate(t, totalCents) {
    if (!t.rateEl) return;
    var eff = effectiveRate(totalCents);
    if (eff == null) { t.rateEl.textContent = ''; return; }
    // Tier name is API-controlled, so it is escaped before entering innerHTML.
    // If the shared escaper somehow hasn't loaded, drop the name rather than
    // throw (which would take the points estimate down with it).
    var tierName = (rate && rate.tierName) || '';
    var out = t.ratePattern
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .split('[rate]').join(eff.toLocaleString())
      .split('[tier]').join(window.KKEsc ? window.KKEsc(tierName) : '');
    // A guest (and a member whose /balance came back without a tier) has no
    // tier name, which leaves the pattern's qualifier clause dangling —
    // "15 pts per $1 ·". Collapse the whitespace the empty token left behind
    // and drop a now-trailing separator so the line still reads as a sentence.
    if (!tierName) out = out.replace(/\s+/g, ' ').replace(/[·|\-–—,]\s*$/, '').trim();
    t.rateEl.innerHTML = out;
  }

  function compose(pattern, pillHtml) {
    // Pattern is a merchant-controlled inline_richtext setting (sanitised by
    // Shopify to a safe tag allow-list before it ever reaches us), so render
    // it raw to preserve <a>, <strong>, etc. Transform **bold** → <strong>,
    // inject the points pill, then fill the live second-order bonus from
    // /program ([second_order], or the legacy [signup] name — never a
    // hardcoded number; blank until the program loads).
    var html = pattern.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    var bonus = (rate && rate.secondOrder) ? '<strong>' + Number(rate.secondOrder).toLocaleString() + '</strong>' : '…';
    return html.replace('[points]', pillHtml).replace(/\[second_order\]|\[signup\]/g, bonus);
  }

  function renderTracker(t) {
    var copy = t.copyEl;
    if (!copy) return;
    var totalCents = (typeof t.getTotal === 'function') ? (t.getTotal() | 0) : 0;
    var pts = pointsFor(totalCents, typeof t.getLines === 'function' ? t.getLines() : null);
    renderRate(t, totalCents);
    if (pts == null) {
      // Loading: coin + a shimmer bar in place of the number (never a bare
      // "…", which reads as broken copy — "You'll earn … Polco Points").
      t.el.classList.add('is-loading');
      var loadingPill = '<span class="kkumi-earn-points__pill is-loading" aria-busy="true">' +
        (t.showCoin ? COIN_SVG : '') + '<span class="kk-skel kkumi-earn-points__count-skel" aria-hidden="true"></span></span>';
      copy.innerHTML = compose(t.pattern, loadingPill);
      return;
    }
    t.el.classList.remove('is-loading');
    if (rate && rate.notClub && t.isCustomer && t.joinPattern && window.KKEsc) {
      // The number stays — it's what joining would earn them on this order —
      // but the whole line becomes the join link. Merchant text is plain
      // `text`, so it is escaped (KKEsc, head-deferred before this file)
      // before the pill is injected.
      if (t.rateEl) t.rateEl.textContent = '';
      var esc = window.KKEsc;
      var joinPill = '<span class="kkumi-earn-points__pill">' + (t.showCoin ? COIN_SVG : '') +
        '<span class="kkumi-earn-points__count ' + t.countClass + '">' + pts + '</span></span>';
      var joinText = esc(t.joinPattern).split('[points]').join(joinPill);
      var joinLink = '<a class="kkumi-earn-points__join" href="' + esc(t.joinUrl) + '">' +
        (t.joinLinkLabel ? esc(t.joinLinkLabel) : joinText) + '</a>';
      copy.innerHTML = t.joinLinkLabel ? joinText + ' ' + joinLink : joinLink;
      return;
    }
    if (pts === 0 && t.isCustomer && t.zeroPattern) {
      copy.innerHTML = t.zeroPattern.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      return;
    }
    var pill = '<span class="kkumi-earn-points__pill">' + (t.showCoin ? COIN_SVG : '') +
      '<span class="kkumi-earn-points__count ' + t.countClass + '">' + pts + '</span></span>';
    copy.innerHTML = compose(t.pattern, pill);
  }

  function renderAll() { trackers.forEach(renderTracker); }
  function renderScope(scope) {
    trackers.forEach(function (t) { if (t.scope === scope) renderTracker(t); });
  }

  // Points are earned on the discounted subtotal minus excluded add-ons
  // (e.g. signature on delivery) — mirror that in the preview. cart.total_price
  // already reflects discounts; subtract the excluded lines' final prices.
  function eligibleCartCents(cart) {
    lastCartLines = (cart.items || []).filter(function (item) {
      return !(rate && rate.excluded.indexOf(String(item.variant_id)) !== -1);
    }).map(function (item, i) {
      return { id: item.key || String(i), productId: String(item.product_id), variantId: String(item.variant_id), qty: item.quantity, cents: item.final_line_price };
    });
    var total = cart.total_price;
    if (rate && rate.excluded.length && cart.items) {
      cart.items.forEach(function (item) {
        if (rate.excluded.indexOf(String(item.variant_id)) !== -1) {
          total -= item.final_line_price;
        }
      });
    }
    return total < 0 ? 0 : total;
  }

  function refreshCartTotal() {
    if (!trackers.some(function (t) { return t.scope === 'cart'; })) return;
    fetch(shopifyRoot() + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        lastCartTotal = eligibleCartCents(cart);
        renderScope('cart');
        notifyCartTotal();
      }).catch(function () {});
  }

  // Let other widgets (e.g. the cart wallet's tier-progress projection) react
  // to the eligible cart total without re-fetching /cart.js themselves.
  function notifyCartTotal() {
    try {
      document.dispatchEvent(new CustomEvent('kkumi:cart-total', { detail: { cents: cartCents() } }));
    } catch (e) { /* CustomEvent unsupported — getter still works */ }
  }

  function installCartListeners() {
    if (cartListenersInstalled) return;
    cartListenersInstalled = true;
    var debounce;
    function schedule() { clearTimeout(debounce); debounce = setTimeout(refreshCartTotal, 350); }

    var form = document.querySelector('form[action^="/cart"]');
    if (form) new MutationObserver(schedule).observe(form, { childList: true, subtree: true });
    var contents = document.querySelector('.js-contents');
    if (contents) new MutationObserver(schedule).observe(contents, { childList: true, subtree: true });
    document.addEventListener('cart:refresh', schedule);
    document.addEventListener('cart:added', schedule);

    // Dawn's cartUpdate pub/sub carries the freshly-rendered cart (with
    // total_price + items), so we can update INSTANTLY from it — no debounced
    // /cart.js round-trip. This is what keeps the wallet's tier projection in
    // lock-step with the cart the way the free-shipping bar is (it also reads
    // cartUpdate). Falls back to the debounced fetch if the payload is thin.
    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, function (event) {
          var cart = event && event.cartData;
          if (cart && typeof cart.total_price === 'number' && Array.isArray(cart.items)) {
            lastCartTotal = eligibleCartCents(cart);
            renderScope('cart');
            notifyCartTotal();
          } else {
            schedule();
          }
        });
      }
    } catch (e) { /* pub/sub unavailable — observers + custom events still fire */ }
  }

  function installProductListener(t) {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined' || !PUB_SUB_EVENTS.variantChange) return;
    subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
      var v = event && event.data && event.data.variant;
      if (!v || typeof v.price !== 'number') return;
      var sectionId = t.el.dataset.sectionId;
      // If the event carries a section id, only the matching tracker reacts.
      // Otherwise every product tracker on the page updates (single-PDP case).
      if (sectionId && event.data.sectionId && sectionId !== event.data.sectionId) return;
      t.currentPriceCents = v.price;
      if (v.id != null) t.variantId = String(v.id);
      renderTracker(t);
    });
  }

  function register(el) {
    if (el.__kkumiEarnRegistered) return;
    el.__kkumiEarnRegistered = true;

    var t = {
      el: el,
      copyEl: el.querySelector('.kkumi-earn-points__copy'),
      scope: el.dataset.scope || 'cart',
      pattern: el.dataset.pattern || "You'll earn [points] Polco Points.",
      zeroPattern: el.dataset.zeroPattern || '',
      isCustomer: el.dataset.isCustomer === 'true',
      joinPattern: el.dataset.joinPattern || '',
      joinLinkLabel: el.dataset.joinLinkLabel || '',
      joinUrl: el.dataset.joinUrl || '/pages/kkumi-club#kkumi-club-join',
      showCoin: el.dataset.showCoin !== 'false',
      countClass: el.dataset.countClass || '',
      ratePattern: el.dataset.ratePattern || '',
      rateEl: el.querySelector('[data-kk-earn-rate]')
    };

    if (t.scope === 'cart') {
      var seedCart = parseInt(el.dataset.initialTotalCents, 10);
      if (Number.isFinite(seedCart) && lastCartTotal == null) lastCartTotal = seedCart;
      t.getTotal = function () { return lastCartTotal == null ? 0 : lastCartTotal; };
      t.getLines = function () { return lastCartLines; };
      installCartListeners();
    } else if (t.scope === 'product') {
      t.currentPriceCents = parseInt(el.dataset.variantPrice, 10) || 0;
      t.productId = el.dataset.productId || '';
      t.variantId = el.dataset.variantId || '';
      t.getTotal = function () { return t.currentPriceCents; };
      // One unit of the shown variant: what an item campaign on it adds.
      t.getLines = function () {
        return t.productId || t.variantId ? [{ id: 'pdp', productId: t.productId, variantId: t.variantId, qty: 1, cents: t.currentPriceCents }] : null;
      };
      installProductListener(t);
    } else if (t.scope === 'bundle') {
      var seedBundle = parseInt(el.dataset.initialTotalCents, 10);
      t.currentTotalCents = Number.isFinite(seedBundle) ? seedBundle : 0;
      t.currentLines = null;
      t.getTotal = function () { return t.currentTotalCents; };
      t.getLines = function () { return t.currentLines; };
    }

    trackers.push(t);
    renderTracker(t);
    fetchRate().then(function () { renderTracker(t); });
  }

  // `lines` (optional): [{ id, productId, variantId, qty, cents }] in storefront
  // cents, so a campaign tied to an item in the bundle counts.
  function setTotal(el, cents, lines) {
    var t = null;
    for (var i = 0; i < trackers.length; i++) { if (trackers[i].el === el) { t = trackers[i]; break; } }
    if (!t || t.scope !== 'bundle') return;
    t.currentTotalCents = cents | 0;
    t.currentLines = Array.isArray(lines) ? lines : null;
    renderTracker(t);
  }

  // ── Product-page campaign callout ([data-kk-campaign-callout]) ──
  // One chip per live campaign tied to this product or the chosen variant (two
  // at most, biggest first), worded from the theme editor's patterns. Shown to a
  // signed-in member only when they can earn it; a guest sees each one with the
  // condition spelled out (they may be an Idol who hasn't signed in).
  function calloutHTML(c) {
    var d = c.el.dataset;
    var esc = window.KKEsc;
    if (!rate || !esc) return '';
    var price = shopCents(c.priceCents) || 0;
    var list = rate.campaigns.filter(function (x) {
      return x.rule && (x.rule.p[c.productId] || x.rule.v[c.variantId]) && (!x.audience || !rate.loggedIn || eligible(x));
    }).map(function (x) {
      return { x: x, size: x.type === 'bonus' ? x.value : pts(price, rate.multiplier * (x.value - 1)) };
    }).sort(function (a, b) { return b.size - a.size; }).slice(0, 2);
    return list.map(function (e) {
      var x = e.x, a = x.audience, notes = [];
      var pattern = x.type === 'bonus' ? (x.scope === 'items' ? d.patternPerItem : d.patternBonus) : (x.scope === 'items' ? d.patternItemMult : d.patternOrderMult);
      // Patterns are inline_richtext (sanitised by Shopify); the numbers are ours.
      var main = String(pattern || '').replace(/\[points\]/g, Math.trunc(x.value).toLocaleString()).replace(/\[multiplier\]/g, String(x.value));
      if (x.rule.minQty > 1 && d.noteQty) notes.push(esc(d.noteQty).split('[qty]').join(String(x.rule.minQty)));
      if (a && a.tier_names && a.tier_names.length && d.noteTiers) notes.push(esc(d.noteTiers).split('[tiers]').join(esc(a.tier_names.join(', '))));
      if (a && a.orders === 'first' && d.noteFirst) notes.push(esc(d.noteFirst));
      if (a && a.orders === 'lapsed' && d.noteLapsed) notes.push(esc(d.noteLapsed).split('[days]').join(String(Number(a.lapsed_days) || '')));
      if (a && a.once_per_member && d.noteOnce) notes.push(esc(d.noteOnce));
      if (x.endsAt && d.noteEnds) notes.push(esc(d.noteEnds).split('[date]').join(esc(new Date(x.endsAt).toLocaleDateString(undefined, { day: 'numeric', month: 'short' }))));
      // Text only: the host row's ✦ already marks it (owner, 28 Oct 2026: no repeated icon).
      return '<span class="kkumi-campaign-callout__row">' +
        '<span class="kkumi-campaign-callout__text">' + main +
        (notes.length ? ' <span class="kkumi-campaign-callout__note">' + notes.join(' · ') + '</span>' : '') + '</span></span>';
    }).join('');
  }

  function renderCallout(c) {
    var html = calloutHTML(c);
    // Write only on change: a MutationObserver over the page must never see a no-op write.
    if (c.el.__kkHtml !== html) { c.el.innerHTML = html; c.el.__kkHtml = html; }
    c.el.hidden = !html;
  }
  function renderCallouts() { callouts.forEach(renderCallout); }

  function registerCallout(el) {
    if (el.__kkCallout) return;
    el.__kkCallout = true;
    if (el.dataset.demo === 'true') return; // the theme editor's sample stays as rendered
    var c = { el: el, productId: el.dataset.productId || '', variantId: el.dataset.variantId || '', priceCents: parseInt(el.dataset.variantPrice, 10) || 0 };
    callouts.push(c);
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        var v = event && event.data && event.data.variant;
        if (!v) return;
        if (el.dataset.sectionId && event.data.sectionId && el.dataset.sectionId !== event.data.sectionId) return;
        if (v.id != null) c.variantId = String(v.id);
        if (typeof v.price === 'number') c.priceCents = v.price;
        renderCallout(c);
      });
    }
    fetchRate().then(function () { renderCallout(c); });
  }

  function scan(root) {
    (root || document).querySelectorAll('.kkumi-earn-points[data-scope]').forEach(register);
    (root || document).querySelectorAll('[data-kk-campaign-callout]').forEach(registerCallout);
  }

  function init() {
    // Pill / coin / skeleton styling lives in assets/kkumi-earn-points.css
    // (loaded globally), so it's parsed before first paint — no JS injection.
    scan(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  // Re-scan when sections re-render in the theme editor or via AJAX.
  document.addEventListener('shopify:section:load', function (e) { scan(e.target || document); });

  // For consumers that render their own UI (e.g. the JS-built bundle block):
  // returns the same tier-adjusted points number every snippet tracker shows,
  // resolving once the rewards app has responded. `cents` is a storefront
  // amount (shopper's currency), like every other total handed to this
  // module — the shop-currency conversion happens here. Synchronous compute() is
  // also exposed for code that already has a render loop and can re-call.
  function compute(cents, lines) {
    return pointsFor(cents, lines);
  }
  function computeAsync(cents, lines) {
    return fetchRate().then(function () { return compute(cents, lines); });
  }

  // Eligible cart total — what counts toward points/tier spend — in SHOP-
  // currency cents, the unit tier thresholds are in. Null until the
  // seed/first fetch resolves, or when it can't be converted.
  function cartCents() { return lastCartTotal == null ? null : shopCents(lastCartTotal); }

  // The Polco Points coin markup — so runtime-built UIs (the FBT block) show
  // the same badge without re-declaring the SVG.
  function coinHTML() { return COIN_SVG; }

  return { register: register, setTotal: setTotal, refresh: renderAll, scan: scan, compute: compute, computeAsync: computeAsync, cartCents: cartCents, coinHTML: coinHTML, effectiveRate: effectiveRate };
})();

